<template>
  <div class="product__main-slider" ref="mainSlider" id="product__main-slider">
    <template v-if="media.length">
      <sib-carousel
        :preload-first-slide="true"
        :items="mediaWithAdditionProperties"
        :index="selectedSlideIndex"
        :options="carouselOptions"
        @slide-change="onSlideChange"
        :disable-swipe-slides="[mediaWithAdditionProperties.length]"
        ref="slider"
      >
        <template #customSlide>
          <div
            class="carousel__slide product__preview-slide"
            :id="PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID"
          ></div>
        </template>
      </sib-carousel>
      <div
        v-if="!deviceStore.isMobile"
        class="product__zoom"
        @mousemove="onMouseMove"
        @mouseleave="onMouseLeave"
        v-show="selectedSlideIndex != media.length && !currentActiveVideo"
      >
        <canvas ref="zoomCanvas"></canvas>
      </div>
    </template>
    <sib-skeleton-loading
      v-else
      size="square"
      round="sm"
    ></sib-skeleton-loading>
    <RatePreviewAI />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, reactive, Ref } from '@vue/reactivity'
import { onMounted, onUnmounted, watch } from 'vue'
import { Media } from '@/types'
import useDeviceStore from '@/store/device'
import type Carousel from '@/ui-kits/Carousel.vue'
import EventBus from '@/services/eventbus'
import RatePreviewAI from './RatePreviewAI.vue'
import {
  EVENT_PRODUCT_UPDATE_MAIN_SLIDER,
  PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID,
  PRODUCT_MEDIA_VIDEO,
} from '@/utils'

const props = withDefaults(
  defineProps<{
    media: Media[]
    selectedSlideIndex: number
  }>(),
  {
    media: () => [],
    selectedSlideIndex: 0,
  }
)
const deviceStore = useDeviceStore()

const slider = ref<typeof Carousel | null>(null)

const carouselOptions = {
  Panzoom: {
    touch: deviceStore.isMobile,
  },
}

const mediaWithAdditionProperties = computed(() => {
  const foundImageMedia = props.media.find(
    (media) => media.mediaContentType !== PRODUCT_MEDIA_VIDEO
  )
  return props.media.map((media, index) => {
    const additionProperties = {
      mediaContentType: media.mediaContentType,
      loading: index > 0 ? 'lazy' : null,
      'data-fancybox': 'productSlider',
      'data-src':
        deviceStore.isMobile && media.mediaContentType === PRODUCT_MEDIA_VIDEO
          ? media.video.url
          : media.image.src,
      placeholderSrc: index > 0 ? media.image.src : undefined,
      placeholderSources:
        index > 0
          ? [
              {
                size: 100,
              },
            ]
          : undefined,
    }
    if (
      deviceStore.isMobile &&
      foundImageMedia &&
      media.mediaContentType === PRODUCT_MEDIA_VIDEO
    ) {
      media.image.src = foundImageMedia.image.src
    }
    if (
      !deviceStore.isMobile &&
      media.mediaContentType === PRODUCT_MEDIA_VIDEO
    ) {
      return Object.assign({}, media, additionProperties)
    }
    return { image: Object.assign({}, media.image, additionProperties) }
  })
})

const emit = defineEmits<{
  (e: 'change', index: number): void
}>()

// zoom function
interface preloadedImage {
  image: HTMLImageElement
  loaded: boolean
}
const zoomCanvas: Ref<HTMLCanvasElement | null> = ref(null)
const preloadedImages: {
  [key: string]: preloadedImage
} = reactive({})

const preloadImage = (imageSrc: string) => {
  const newImage = new Image()
  const preloadedImage: preloadedImage = {
    image: newImage,
    loaded: false,
  }
  newImage.addEventListener('load', () => {
    preloadedImage.loaded = true
  })
  newImage.src = imageSrc
  return preloadedImage
}

onMounted(() => {
  // preload images only on desktop
  // preload the image for zoom whenever current active image changed
  if (!deviceStore.isMobile) {
    watch(currentActiveMedia, () => {
      const imageSrc = currentActiveMedia.value?.image?.src
      if (!imageSrc) return
      let preloadedImage = preloadedImages[imageSrc]
      if (!preloadedImage) {
        preloadedImage = preloadImage(imageSrc)
        preloadedImages[imageSrc] = preloadedImage
      }
    })
  }

  EventBus.on(EVENT_PRODUCT_UPDATE_MAIN_SLIDER, updateMainSlider)
})
onUnmounted(() => {
  EventBus.off(EVENT_PRODUCT_UPDATE_MAIN_SLIDER, updateMainSlider)
})

function updateMainSlider() {
  slider.value?.update()
}

const onSlideChange = (index: number) => {
  emit('change', index)
}

const currentActiveMedia = computed(() => {
  return props.media[props.selectedSlideIndex]
})

const currentActiveVideo = computed(() => {
  return (
    props.media[props.selectedSlideIndex].mediaContentType ===
    PRODUCT_MEDIA_VIDEO
  )
})

const mainSlider: Ref<HTMLElement | null> = ref(null)
const onMouseMove = (event: MouseEvent) => {
  if (!zoomCanvas.value || !mainSlider.value) return
  const ctx = zoomCanvas.value.getContext('2d')
  const imageSrc = currentActiveMedia.value?.image?.src
  if (!imageSrc) return
  let preloadedImage = preloadedImages[imageSrc]
  if (!preloadedImage) {
    preloadedImage = preloadImage(imageSrc)
    preloadedImages[imageSrc] = preloadedImage
  }
  if (!preloadedImage?.loaded) return
  const originalWidth = preloadedImage.image.width
  const originalHeight = preloadedImage.image.height
  zoomCanvas.value.width = originalWidth
  zoomCanvas.value.height = originalHeight
  ctx?.drawImage(preloadedImage.image, 0, 0)

  const { top, left, width } = mainSlider.value.getBoundingClientRect()
  const zoomRatio = originalWidth / width // should > 1
  const zoomPoint = {
    x: event.clientX - left,
    y: event.clientY - top,
  }
  zoomCanvas.value.style.transform = `translate3d(${
    zoomPoint.x * (1 - zoomRatio)
  }px, ${zoomPoint.y * (1 - zoomRatio)}px, 0)`
}

const onMouseLeave = () => {
  if (!zoomCanvas.value) return
  const ctx = zoomCanvas.value.getContext('2d')
  ctx?.clearRect(0, 0, zoomCanvas.value.width, zoomCanvas.value.height)
}
</script>

<style lang="scss">
.product__main-slider {
  position: relative;
  width: 100%;

  @include media-up(501px) {
    --carousel-margin-bottom: 0px;

    .carousel__dots {
      display: none;
    }
  }

  @include media-md-up {
    --carousel-margin-bottom: 30px;

    .carousel__dots {
      display: flex;
    }
  }

  @include media-lg-up {
    --carousel-margin-bottom: 0px;

    .carousel__dots {
      display: none;
    }
  }

  img {
    object-fit: contain;
    aspect-ratio: 1 / 1;
  }
}

.product__zoom {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - var(--carousel-margin-bottom));
  z-index: 1;
  overflow: hidden;
  cursor: zoom-in;

  canvas {
    position: absolute;
    top: 0;
    left: 0;
  }
}

.product__preview-slide {
  display: none;
  margin: auto;
  &.active {
    display: block;
  }
}
</style>
